import PptxGenJS, { Slide } from "pptxgenjs"
import * as AllocationGen from "./allocation-generator"
import * as AutofinancingGen from "./autofinancing-generator"
import * as InvestimentGen from "./investment-generator"
import * as OperationGen from "./operation-generator"
import * as TaxGen from "./tax-generator"
import { ReportConfiguration } from "../../configuration"
import { MasterSlide } from "../pptx-configuration/master-slides"
import { Placeholder } from "../pptx-configuration/placeholders"
import { DocumentSection } from "../types"
import { formatTitle } from "../utils"
import * as Model from "../../../../model"
import * as Constants from "./../pptx-configuration/constants"
import * as ComparaisonGen from "./comparaison-generator"
import * as SynthesisGen from "./synthesis-generator/synthesis-generator"
import { createScenarioContext } from "../scenario-context"

export function checkObservationSection(configuration: ReportConfiguration): boolean {
	return OperationGen.checkOperationSection(configuration, true)
	|| OperationGen.checkOperationSection(configuration, false)
	|| OperationGen.checkProgressOperationSection(configuration, true)
	|| OperationGen.checkProgressOperationSection(configuration, false)
	|| AutofinancingGen.checkAutoFinancingSection(configuration)
	|| InvestimentGen.checkInvestmentSection(configuration)
	|| TaxGen.checkTaxSection(configuration)
	|| AllocationGen.checkAllocationsSection(configuration)
	|| ComparaisonGen.checkComparaisonSection(configuration)
	|| SynthesisGen.checkSynthesisSection(configuration)
}

/**
 * Creates slides about retrospective.
 * @param pptx
 * @param configuration
 * @param sectionNumber
 */
export async function createObservationSection(
	pptx: PptxGenJS,
	configuration: ReportConfiguration,
	numbering: number[],
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	loans:any,
	propsloan:any,
	isGabaritDob:boolean
): Promise<DocumentSection> {
	const hasOperationReceiptsSection = OperationGen.checkOperationSection(
		configuration, true)
	const hasOperationProgressReceiptsSection = OperationGen.checkProgressOperationSection(
		configuration, true)
	const hasOperationExpenseSection = OperationGen.checkOperationSection(
		configuration, false)
	const hasOperationProgressExpenseSection = OperationGen.checkProgressOperationSection(
		configuration, false)
	const hasAutoFinancingSection = AutofinancingGen.checkAutoFinancingSection(
		configuration)
	const hasInvestmentSection = InvestimentGen.checkInvestmentSection(
		configuration)
	const hasTaxSection = TaxGen.checkTaxSection(configuration)
	const hasAllocationsSection = AllocationGen.checkAllocationsSection(
		configuration)
	const hasComparaisonSection = ComparaisonGen.checkComparaisonSection(configuration);
	const hasSynthesisSection = SynthesisGen.checkSynthesisSection(configuration);
	const years = configuration.generalities.years

	if (!(hasOperationReceiptsSection
		|| hasOperationExpenseSection
		|| hasAutoFinancingSection
		|| hasInvestmentSection
		|| hasTaxSection
		|| hasAllocationsSection
		|| hasComparaisonSection
		|| hasSynthesisSection
		|| hasOperationProgressReceiptsSection
		|| hasOperationProgressExpenseSection))
		return { slides: [], title: null }

	// Même scénario et même moteur de calcul que la Synthèse du scénarii de
	// fonctionnement pour toutes les sections du fonctionnement, sur le dernier
	// scénario validé
	const scenarioContext = await createScenarioContext(localAuthority, ppi, propsloan)

	const summarySlide = pptx.addNewSlide(MasterSlide.Default)
	const title = formatTitle("Constat rétrospectif et prospectif", numbering)
	summarySlide.addText(title, { placeholder: Placeholder.Title })
	const slides: Slide[] = []
	const subTitles: string[] = []

	let subsectionNumber = 0
	if (hasOperationReceiptsSection) {
		const receiptsOperationSection = OperationGen.createOperationBudgetSection(
			pptx,
			configuration,
			numbering.concat(++subsectionNumber),
			localAuthority,
			scenario,
			ppi,
			loanDecompositions,
			years,
			true,
			isGabaritDob,
			scenarioContext
		)
		receiptsOperationSection.slides.forEach(s => slides.push(s))
		subTitles.push(receiptsOperationSection.title)
	}

	if (hasOperationProgressReceiptsSection) {
		const progressReceiptsOperationSection = OperationGen.createProgressOperationBudgetSection(
			pptx,
			configuration,
			numbering.concat(++subsectionNumber),
			localAuthority,
			scenario,
			ppi,
			loanDecompositions,
			years,
			true,
			scenarioContext
		)
		progressReceiptsOperationSection.slides.forEach(s => slides.push(s))
		subTitles.push(progressReceiptsOperationSection.title)
	}

	if (hasOperationExpenseSection) {
		const expenseOperationSection = OperationGen.createOperationBudgetSection(
			pptx,
			configuration,
			numbering.concat(++subsectionNumber),
			localAuthority,
			scenario,
			ppi,
			loanDecompositions,
			years,
			false,
			isGabaritDob,
			scenarioContext
		)
		expenseOperationSection.slides.forEach(s => slides.push(s))
		subTitles.push(expenseOperationSection.title)
	}

	if (hasOperationProgressExpenseSection) {
		const progressExpenseOperationSection = OperationGen.createProgressOperationBudgetSection(
			pptx,
			configuration,
			numbering.concat(++subsectionNumber),
			localAuthority,
			scenario,
			ppi,
			loanDecompositions,
			years,
			false,
			scenarioContext
		)
		progressExpenseOperationSection.slides.forEach(s => slides.push(s))
		subTitles.push(progressExpenseOperationSection.title)
	}


	if (hasAutoFinancingSection) {
		const autoFinancingSection = await AutofinancingGen.createAutoFinancingSection(
			pptx,
			configuration,
			numbering.concat(++subsectionNumber),
			localAuthority,
			scenario,
			ppi,
			loanDecompositions,
			years,
			loans,
			propsloan,
			scenarioContext
		)
		autoFinancingSection.slides.forEach(s => slides.push(s))
		subTitles.push(autoFinancingSection.title)
	}

	if (hasInvestmentSection) {
		const investmentSection = InvestimentGen.createInvestmentSection(
			pptx,
			configuration,
			numbering.concat(++subsectionNumber),
			localAuthority,
			years,
			ppi,
			loans
		)
		investmentSection.slides.forEach(s => slides.push(s))
		subTitles.push(investmentSection.title)
	}

	if (hasTaxSection) {
		const taxSection = TaxGen.createTaxSection(
			pptx,
			configuration,
			numbering.concat(++subsectionNumber),
			localAuthority,
			years,
			scenario
		)
		taxSection.slides.forEach(s => slides.push(s))
		subTitles.push(taxSection.title)
	}

	if (hasAllocationsSection) {
		const allocationsSection = AllocationGen.createAllocationsSection(
			pptx,
			configuration,
			numbering.concat(++subsectionNumber),
			localAuthority,
			years
		)
		allocationsSection.slides.forEach(s => slides.push(s))
		subTitles.push(allocationsSection.title)
	}

	if(hasComparaisonSection){
		// « Prévisions N » : scénario validé de l'année de référence (N)
		const comparaisonScenarioContext = await createScenarioContext(
			localAuthority, ppi, propsloan, configuration.generalities.referenceYear)
		const comparaisonSection = await ComparaisonGen.createComparaisonSection(
			pptx,
			configuration,
			numbering.concat(++subsectionNumber),
			years,
			localAuthority,
			scenario,
			comparaisonScenarioContext
		)
		comparaisonSection.slides.forEach(s=>slides.push(s))
		subTitles.push(comparaisonSection.title)
	}

	if(hasSynthesisSection){
		const synthesisSection = SynthesisGen.createSynthesisSection(
			pptx,
			configuration,
			numbering,
			localAuthority
		)
		synthesisSection.slides.forEach(s=>slides.push(s))
		subTitles.push(synthesisSection.title)
	}

	summarySlide.addText(
		subTitles.map(t => ({ text: t, options: { breakLine: true }})),
		Constants.getTextPlacementOptions
	)

	return { title, slides }
}



// WEBPACK FOOTER //
// ./src/component/management-report/generator/observation-generator/index.tsx